import { GlobalState, type ITenant } from '@/global';
import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { IdentityApi, LoginApi, PermissionsApi, type IIdentityResponse, type IInstall, type ILoginResponse } from '@/openapi-clients-generated/auth';
import { useStore } from '@/store';
import { dataFrom, OpenApiError } from '@signal24/openapi-client-codegen';
import { ref, watch } from 'vue';
import type { Router } from 'vue-router';
import { DataCache } from '../data/cache';
import { Logger } from './logger';

const logger = Logger.createScoped('AuthService');

class AuthService {
    authStorage: Storage = localStorage;
    logoutCause: string | undefined;
    renewPromise: Promise<void> | null = null;

    userId = ref<string | null>(sessionStorage.getItem('zs:auth:userId'));
    tenantId = ref<string | null>(sessionStorage.getItem('zs:auth:tenantId') ?? localStorage.getItem('zs:auth:tenantId'));
    impersonatorUserId = ref<string | null>(sessionStorage.getItem('zs:auth:impersonatorUserId'));
    impersonatorTenantId = ref<string | null>(sessionStorage.getItem('zs:auth:impersonatorTenantId'));
    rememberMeExpiresAt = ref<number | null>(parseInt(localStorage.getItem('zs:auth:rememberMeExpiresAt') || '') || null);

    constructor() {
        this.setupRefWatchers();
    }

    // COMPUTED/WATCHED REFS

    private setupRefWatchers() {
        watch(this.userId, newUserId => {
            this.persistValueToStorage(sessionStorage, 'zs:auth:userId', newUserId);
            if (newUserId) this.setupAuthedRefWatchers();
        });
        watch(this.tenantId, newTenantId => {
            this.persistValueToStorage(sessionStorage, 'zs:auth:tenantId', newTenantId);
            if (!this.impersonatorTenantId.value) {
                this.persistValueToStorage(this.authStorage, 'zs:auth:tenantId', newTenantId);
            }
        });
        watch(this.impersonatorUserId, newImpersonatorUserId =>
            this.persistValueToStorage(sessionStorage, 'zs:auth:impersonatorUserId', newImpersonatorUserId)
        );
        watch(this.impersonatorTenantId, newImpersonatorTenantId =>
            this.persistValueToStorage(sessionStorage, 'zs:auth:impersonatorTenantId', newImpersonatorTenantId)
        );
        watch(this.rememberMeExpiresAt, newExpiresAt =>
            this.persistValueToStorage(localStorage, 'zs:auth:rememberMeExpiresAt', newExpiresAt ? String(newExpiresAt) : null)
        );

        watch(GlobalState.user, () => this.setupAuthedRefWatchers());
    }

    private hasSetupAuthedRefWatchers = false;
    private setupAuthedRefWatchers() {
        if (this.hasSetupAuthedRefWatchers) return;
        this.hasSetupAuthedRefWatchers = true;
        watch(
            [DataCache.getLazyRef('talkAccess'), GlobalState.permissions],
            ([talkAccess, permissions]) => {
                if (permissions) {
                    permissions.talk = talkAccess?.length || GlobalState.tenant.value?.isAdmin ? {} : undefined;
                }
            },
            { immediate: true }
        );
    }

    private persistValueToStorage(storage: Storage, key: string, value: string | null) {
        if (value === null) {
            storage.removeItem(key);
        } else {
            storage.setItem(key, value);
        }
    }

    // GETTERS

    get logoutCauseOnce() {
        const cause = this.logoutCause;
        this.logoutCause = undefined;
        return cause;
    }

    // INIT

    get activeJwt() {
        return this.authStorage.getItem('zs:auth:sessionJwt');
    }

    get activeJwtExp() {
        return Number.parseInt(this.authStorage.getItem('zs:auth:sessionJwtExp')!) ?? 0;
    }

    // STATE SETUP

    setInstall(install: Pick<IInstall, 'id'>) {
        this.authStorage = sessionStorage;
        GlobalState.install.value = install;
        logger.info(`Set install to ${install.id}`);
    }

    setUser(user: IIdentityResponse) {
        this.userId.value = user.id;
        GlobalState.user.value = user;
        logger.info(`Set user to ${user.id}`);
    }

    setTenant(tenant: Pick<ITenant, 'id' | 'name' | 'isAdmin'>) {
        this.tenantId.value = tenant.id;
        GlobalState.tenant.value = tenant;
        logger.info(`Set tenant to ${tenant.id}`);
    }

    setDefaultTenant() {
        const user = GlobalState.user.value!;
        let tenant = this.tenantId.value && user.tenants.find(t => t.id === this.tenantId.value);
        if (!tenant) {
            tenant = user.tenants.find(t => t.isFavorite);
        }
        if (!tenant) {
            tenant = user.tenants[0];
        }
        this.setTenant(tenant!);
    }

    // IMPERSONATION

    setImpersonatedUserId(id: string) {
        if (this.impersonatorUserId.value) throw new Error('Cannot set impersonator user when one is already set');
        this.impersonatorUserId.value = this.userId.value;
        this.userId.value = id;
        logger.info(`Set impersonated user ID to ${id}`);
    }

    clearImpersonatedUser() {
        if (!this.impersonatorUserId.value) throw new Error('Cannot clear impersonator user when one is not set');
        this.userId.value = this.impersonatorUserId.value;
        this.impersonatorUserId.value = null;
        logger.info('Cleared impersonator user ID');
    }

    setImpersonatedTenantId(id: string) {
        if (this.impersonatorTenantId.value) throw new Error('Cannot set impersonator tenant when one is already set');
        this.impersonatorTenantId.value = this.tenantId.value;
        this.tenantId.value = id;
        logger.info(`Set impersonated tenant ID to ${id}`);
    }

    clearImpersonatedTenant() {
        if (!this.impersonatorTenantId.value) throw new Error('Cannot clear impersonator tenant when one is not set');
        this.tenantId.value = this.impersonatorTenantId.value;
        this.impersonatorTenantId.value = null;
        logger.info('Cleared impersonator tenant ID');
    }

    // SESSION MANAGEMENT

    async processLoginResponse(response: ILoginResponse) {
        this.storeJwtFromResponse(response);
        this.rememberMeExpiresAt.value = response.rememberMeExpiresAt ?? null;

        // if we were told that we have an impersonator tenant ID, set it
        if (response.impersonatorTenantId) {
            this.impersonatorTenantId.value = response.impersonatorTenantId;
        }

        // if we're impersonating, reload the identity with masquerade headers
        if (this.impersonatorTenantId.value || this.impersonatorUserId.value) {
            response.identity = dataFrom(await IdentityApi.getIdentityGetIdentity());
        }

        this.setUser(response.identity);
        this.setDefaultTenant();
        await this.reloadPermissions();
    }

    async renewJwt() {
        if (!this.renewPromise) {
            this.renewPromise = this.renewJwtInternal().finally(() => {
                this.renewPromise = null;
            });
        }
        return this.renewPromise;
    }

    private async renewJwtInternal() {
        try {
            const response = dataFrom(
                await LoginApi.postLoginRenew({
                    body: {
                        browserId: GlobalState.install.value?.id ?? GlobalState.browserId
                    },
                    credentials: 'include'
                })
            );
            this.storeJwtFromResponse(response);
        } catch (err) {
            // if we already get a 401, it's too late
            if (err instanceof OpenApiError && err.response.status === 401) {
                logger.info('JWT renewal failed with 401');
                this.clearStoredJwt();
                return;
            }
            logger.warn('Failed to renew JWT', err);
        }
    }

    async storeJwtFromResponse(response: Pick<ILoginResponse, 'jwt' | 'expiresAt'>) {
        this.authStorage.setItem('zs:auth:sessionJwt', response.jwt);
        this.authStorage.setItem('zs:auth:sessionJwtExp', String(response.expiresAt));
        logger.info('Stored JWT');
    }

    clearStoredJwt(cause?: string) {
        GlobalState.user.value = null;

        if (!GlobalState.install.value) {
            GlobalState.tenant.value = null;
        }

        this.rememberMeExpiresAt.value = null;
        this.authStorage.removeItem('zs:auth:sessionJwt');
        this.authStorage.removeItem('zs:auth:sessionJwtExp');

        if (this.impersonatorTenantId.value) {
            this.clearImpersonatedTenant();
        }

        if (cause) {
            this.logoutCause = cause;
        }

        logger.info('Cleared user session', { cause });
    }

    // LOADERS

    async loadExistingSessionInitial() {
        this.loadStoredJwt();
        return this.loadExistingSession();
    }

    async loadExistingSession() {
        if (!MobileBridge.isWithinZynoSuiteMobile) {
            if (!this.activeJwt) return;
            if (this.activeJwtExp <= Date.now()) {
                logger.info('Stored JWT has expired. Attempting renewal...');
                await this.renewJwt();
                if (!this.activeJwt) return;
                if (this.activeJwtExp <= Date.now()) {
                    logger.info('JWT renewal failed or still expired. Clearing session.');
                    return this.clearStoredJwt();
                }
            }
        }

        try {
            const user = dataFrom(await IdentityApi.getIdentityGetIdentity());
            this.setUser(user);
            this.setDefaultTenant();
            await this.reloadPermissions();
        } catch (err) {
            console.log(err);
            // the 401 from the API middlware will have already cleared the user session before we get here
        }
    }

    private loadStoredJwt() {
        if (!this.activeJwt) return;
        if (this.activeJwtExp <= Date.now() && !this.rememberMeExpiresAt.value) {
            logger.info('Stored JWT has expired and "remember me" expiration is set. Clearing JWT.');
            return this.clearStoredJwt();
        }
        if (!this.tenantId.value) {
            logger.info('No tenant ID found in storage. Clearing stored JWT.');
            return this.clearStoredJwt();
        }
        logger.info('Loaded stored JWT');
    }

    async reloadExistingSessionForTenantSwitch(router: Router) {
        await this.loadExistingSession();

        const currentRoute = router.currentRoute.value;
        const matches = currentRoute.fullPath.match(/\/[0-9a-f-]{36}/);
        if (matches) {
            await router.push(currentRoute.fullPath.substring(0, matches.index!));
        }
    }

    async reloadPermissions(clearBeforeReload = true) {
        if (clearBeforeReload) {
            GlobalState.permissions.value = null;
        }
        try {
            GlobalState.permissions.value = dataFrom(await PermissionsApi.getPermissionsGet());
            logger.info('Loaded user permissions');
        } catch {
            useStore().globalError = 'There was an issue loading user permissions. Please check your Internet connection and refresh the page.';
        }
    }
}

export const Auth = new AuthService();
